import React, { useState, useEffect, useRef } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { reportIssue, ReportIssueData } from "../../Admin-Api/ReportIssue";
import { deleteReportIssueAdmin } from "../../Admin-Api/DeleteReportIssueAsync";


interface CustomJwtPayload {
  RoleId: string;
}

const ReportIssueSkeleton = () => (
  <div className="animate-pulse">
    <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-sm mb-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <div className="h-4 w-20 bg-gray-200 rounded"></div>
          <div className="h-10 w-full bg-gray-200 rounded-lg"></div>
        </div>
        <div className="space-y-2">
          <div className="h-4 w-20 bg-gray-200 rounded"></div>
          <div className="h-10 w-full bg-gray-200 rounded-lg"></div>
        </div>
      </div>
      <div className="mt-4">
        <div className="h-10 w-full bg-gray-200 rounded-lg"></div>
      </div>
    </div>

    <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-sm overflow-hidden">
      <div className="hidden lg:grid bg-gradient-to-r from-gray-50 to-gray-100 p-4 grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.5fr)_minmax(0,2fr)_minmax(0,1fr)] gap-3 border-b">
        {[...Array(7)].map((_, i) => (
          <div key={i} className="h-5 bg-gray-200 rounded"></div>
        ))}
      </div>

      <div className="block lg:hidden p-4 space-y-4 bg-gray-50">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <div className="space-y-2">
              <div className="h-4 w-32 bg-gray-200 rounded"></div>
              <div className="h-4 w-40 bg-gray-200 rounded"></div>
              {[...Array(4)].map((__, j) => (
                <div key={j} className="h-4 bg-gray-200 rounded w-full"></div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const ViewReportIssue: React.FC = () => {
  const navigate = useNavigate();
  const [reportIssueData, setReportIssueData] = useState<ReportIssueData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const lastToastTimeRef = useRef<number>(0);
  const TOAST_THROTTLE_MS = 3000;

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [searchText, setSearchText] = useState("");

  const [originalData, setOriginalData] = useState<ReportIssueData[]>([]);
  const [dateFilteredData, setDateFilteredData] = useState<ReportIssueData[]>([]);

  const handleSelect = (id: number) => {
    setSelectedIds(prev =>
      prev.includes(id)
        ? prev.filter(x => x !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    const currentIds = reportIssueData.map(item => item.id);

    const allSelected = currentIds.every(id => selectedIds.includes(id));

    if (allSelected) {
      setSelectedIds(prev => prev.filter(id => !currentIds.includes(id)));
    } else {
      setSelectedIds(prev => [
        ...new Set([...prev, ...currentIds])
      ]);
    }
  };

  const handleDeleteSelected = async () => {
    try {
      setIsDeleting(true);
      const token = localStorage.getItem("token") || "";

      let success = 0;
      let fail = 0;

      const results = await Promise.allSettled(
        selectedIds.map(id => deleteReportIssueAdmin(id, token))
      );

      results.forEach(res => {
        if (res.status === "fulfilled") success++;
        else fail++;
      });

      toast.success("Report deleted successfully");
      if (fail > 0) toast.warning("Some deletions failed");

      setSelectedIds([]);
      setShowDeleteConfirm(false);
      await fetchReportIssueData();
    } catch {
      toast.error("Delete failed");
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
  applyFilters();
}, [searchText, dateFilteredData]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("No auth token found. Please log in.");
      navigate("/");
    } else {
      try {
        const decodedToken = jwtDecode<CustomJwtPayload>(token);
        if (decodedToken.RoleId !== "1") {
          toast.error("Unauthorized access: RoleId is not 1");
          navigate("/");
        }
      } catch (error) {
        console.error("Error decoding token:", error);
        toast.error("Invalid token. Please log in again.");
        navigate("/");
      }
    }
  }, [navigate]);

  const fetchReportIssueData = async () => {
  setIsLoading(true);

  try {
    const token = localStorage.getItem("token");
    if (!token) throw new Error("No auth token found");

    const data = await reportIssue(token);

    // Sort latest reports first
    const sortedData = [...data].sort(
      (a, b) =>
        new Date(b.createdDate).getTime() -
        new Date(a.createdDate).getTime()
    );

    setOriginalData(sortedData);
    setDateFilteredData(sortedData);
    setReportIssueData(sortedData);

  } catch (error) {
    console.error("Error fetching report issue data:", error);
  } finally {
    setIsLoading(false);
  }
};

  const fetchReportIssueByDateRange = async () => {
    if (!startDate || !endDate) {
      if (!toast.isActive("select-dates")) {
        toast.info("Please select a start date and end date.", {
          toastId: "select-dates",
        });
      }
      return;
    }

    const formattedStartDate = new Date(startDate).toISOString().split("T")[0];
    const formattedEndDate = new Date(endDate).toISOString().split("T")[0];

    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No auth token found");

      const filteredReport = originalData.filter((report) => {
        const created = new Date(report.createdDate).setHours(0, 0, 0, 0);
        const start = new Date(formattedStartDate).setHours(0, 0, 0, 0);
        const end = new Date(formattedEndDate).setHours(23, 59, 59, 999);

        return created >= start && created <= end;
      });

      if (
        filteredReport.length === 0 &&
        Date.now() - lastToastTimeRef.current > TOAST_THROTTLE_MS
      ) {
        toast.info("No data found for the selected date range.", {
          toastId: "no-data",
        });
        lastToastTimeRef.current = Date.now();
      }

      setDateFilteredData(filteredReport);
    } catch (error) {
      console.error("Error fetching report issue data by date range:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReportIssueData();
  }, []);

  const handleReportIssueClick = (report: ReportIssueData) => {
    navigate(`/report-issue-details/${report.id}`, {
      state: { reportIssueData: report },
    });
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  const newStartDate = e.target.value;
  setStartDate(newStartDate);

  if (endDate && endDate < newStartDate) {
    setEndDate("");
  }

  if (!newStartDate) {
    setDateFilteredData(originalData);
  }
};

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  const newEndDate = e.target.value;
  setEndDate(newEndDate);

  if (!newEndDate) {
    setDateFilteredData(originalData);
  }
};

  const totalRecords = reportIssueData.length;

  const applyFilters = () => {
let filtered = dateFilteredData;
    if (searchText.trim()) {
      const text = searchText.toLowerCase();

      filtered = filtered.filter((item) =>
        String(item.userName || "").toLowerCase().includes(text) ||
        String(item.email || "").toLowerCase().includes(text) ||
        String(item.phoneNumber || "").toLowerCase().includes(text) ||
        String(item.subject || "").toLowerCase().includes(text) ||
        String(item.reportContent || "").toLowerCase().includes(text)
      );
    }

    setReportIssueData(filtered);
  };


  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-4 sm:py-6 flex-grow">
        <div className="mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center">
            <span className="bg-blue-600 w-2 h-8 rounded mr-2 sm:mr-3"></span>
            Manage Report Issues
          </h1>
        </div>

        {isLoading ? (
          <ReportIssueSkeleton />
        ) : (
          <>
            <div className="w-full mb-6">
              <div className="w-full bg-gray-100 rounded-xl px-4 py-4">
                <div className="flex items-center justify-between gap-3">
                  <h4 className="text-lg sm:text-xl text-gray-800 font-semibold">
                    Report Issues Management
                  </h4>

                  {totalRecords > 0 && (
                    <span className="shrink-0 px-2 sm:px-3 py-1 bg-blue-100 text-blue-600 rounded-full text-xs sm:text-sm font-medium">
                      {totalRecords} Records
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 shadow-sm mb-6">
              <div className="flex flex-wrap items-end justify-between gap-3">

                {/* LEFT SIDE */}
                <div className="flex flex-wrap items-end gap-3">

                  <div className="flex flex-col">
                    <label className="text-sm text-gray-600 mb-1">Start Date</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={handleStartDateChange}
                      className="w-[140px] px-2 py-1.5 text-sm rounded-md border"
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm text-gray-600 mb-1">End Date</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={handleEndDateChange}
                      className="w-[140px] px-2 py-1.5 text-sm rounded-md border disabled:bg-gray-100"
                    />
                  </div>

                  <button
                    onClick={fetchReportIssueByDateRange}
                    className="h-[34px] px-3 bg-blue-600 text-white text-sm rounded-md"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </button>

                  <input
                    type="text"
                    placeholder="Search"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    className="h-[34px] px-3 text-sm border rounded-md w-[220px]"
                  />

                </div>

                {/* RIGHT SIDE */}
                <div className="ml-auto">
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={selectedIds.length === 0}
                    className="h-[34px] px-3 bg-red-500 text-white text-sm rounded-md disabled:opacity-50"
                  >
                    Delete {selectedIds.length > 0 && `(${selectedIds.length})`} Selected
                  </button>
                </div>

              </div>
            </div>

            {totalRecords > 0 && (
              <>
                <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-sm overflow-hidden">
                  {/* Desktop Table */}
                  <div className="hidden lg:block w-full">
                    <div className="w-full">
                      <div className="bg-blue-500 p-4 grid grid-cols-[40px_minmax(0,1fr)_minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.5fr)_minmax(0,2fr)_minmax(0,1fr)] gap-3 border-b">

                        {/* SELECT ALL */}
                        <div className="flex justify-center items-center">
                          <input
                            type="checkbox"
                            checked={
                              reportIssueData.length > 0 &&
                              reportIssueData.every(item => selectedIds.includes(item.id))
                            }
                            onChange={handleSelectAll}
                          />
                        </div>

                        {[
                          "User Name",
                          "Email",
                          "Phone Number",
                          "Created Date",
                          "Subject",
                          "Report Content",
                          "OS",
                        ].map((header) => (
                          <div
                            key={header}
                            className="text-xs xl:text-sm font-semibold text-black text-center break-words"
                          >
                            {header}
                          </div>
                        ))}
                      </div>

                      <div className="divide-y divide-gray-100">
                        {reportIssueData.map((report) => (
                          <div
                            key={report.id}
                            className="p-4 grid grid-cols-[40px_minmax(0,1fr)_minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.5fr)_minmax(0,2fr)_minmax(0,1fr)] gap-3 hover:bg-blue-50/50 cursor-pointer transition-colors duration-150 items-start" onClick={() => handleReportIssueClick(report)}
                          >
                            <div
                              className="flex justify-center items-center w-[40px]"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                checked={selectedIds.includes(report.id)}
                                onChange={() => handleSelect(report.id)}
                              />
                            </div>
                            <div className="w-full min-w-0 text-center overflow-hidden">
                              <span className="block w-full text-xs xl:text-sm font-semibold break-all whitespace-normal line-clamp-3 hover:line-clamp-none">
                                {report.userName}
                              </span>
                            </div>

                            <div className="w-full min-w-0 text-center overflow-hidden">
                              <span className="block w-full text-xs xl:text-sm font-semibold break-all whitespace-normal line-clamp-3 hover:line-clamp-none">
                                {report.email}
                              </span>
                            </div>

                            <div className="w-full min-w-0 text-center overflow-hidden">
                              <span className="block w-full text-xs xl:text-sm font-semibold break-all whitespace-normal line-clamp-3 hover:line-clamp-none">
                                {report.phoneNumber}
                              </span>
                            </div>

                            <div className="w-full min-w-0 text-center overflow-hidden">
                              <span className="block w-full text-xs xl:text-sm font-semibold break-all whitespace-normal line-clamp-3 hover:line-clamp-none">
                                {report.createdDate}
                              </span>
                            </div>

                            <div className="w-full min-w-0 text-center overflow-hidden">
                              <span className="block w-full text-xs xl:text-sm font-semibold break-all whitespace-normal line-clamp-3 hover:line-clamp-none">
                                {report.subject || "No subject"}
                              </span>
                            </div>

                            <div className="w-full min-w-0 text-center overflow-hidden">
                              <span className="block text-sm font-semibold break-words whitespace-nowrap overflow-hidden text-ellipsis max-w-full">
                                {report.reportContent || "No content"}
                              </span>
                            </div>

                            <div className="w-full min-w-0 text-center overflow-hidden">
                              <span className="block w-full text-xs xl:text-sm font-semibold break-all whitespace-normal line-clamp-3 hover:line-clamp-none">
                                {report.userOperatingSystem
                                  ? report.userOperatingSystem === 1
                                    ? "iOS"
                                    : "Android"
                                  : "web"}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Mobile Cards */}
                  <div className="block lg:hidden p-4 space-y-4 bg-gray-50">
                    {reportIssueData.map((report) => {
                      const osText = report.userOperatingSystem
                        ? report.userOperatingSystem === 1
                          ? "iOS"
                          : "Android"
                        : "web";

                      return (
                        <div
                          key={report.id}
                          onClick={() => handleReportIssueClick(report)}
                          className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 cursor-pointer hover:shadow-md transition"
                        >
                          <div className="flex items-start justify-between gap-3 mb-3">

                            <div className="flex items-start gap-2 w-full">

                              {/* CHECKBOX */}
                              <input
                                type="checkbox"
                                checked={selectedIds.includes(report.id)}
                                onClick={(e) => e.stopPropagation()} // ✅ IMPORTANT
                                onChange={() => handleSelect(report.id)}
                                className="w-4 h-4 mt-1 accent-blue-600"
                              />

                              <div className="min-w-0">
                                <h2 className="text-sm sm:text-base font-bold text-gray-800 break-words">
                                  {report.userName}
                                </h2>
                                <p className="text-xs sm:text-sm break-all mt-1 text-gray-600">
                                  {report.email}
                                </p>
                              </div>

                            </div>

                          </div>

                          <div className="space-y-2 text-sm text-gray-700">
                            <p>
                              <span className="font-semibold text-gray-800">Phone:</span>{" "}
                              {report.phoneNumber}
                            </p>

                            <p>
                              <span className="font-semibold text-gray-800">Created Date:</span>{" "}
                              {report.createdDate}
                            </p>

                            <p>
                              <span className="font-semibold text-gray-800">Subject:</span>{" "}
                              {report.subject || "No subject"}
                            </p>

                            <p>
                              <span className="font-semibold text-gray-800">Report:</span>{" "}
                              {report.reportContent || "No content"}
                            </p>

                            <p>
                              <span className="font-semibold text-gray-800">OS:</span>{" "}
                              {osText}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>


              </>
            )}
          </>
        )}

        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-[320px] shadow-lg">

              <h2 className="text-lg font-semibold text-gray-800 mb-2">
                Delete Reported Issue
              </h2>

              <p className="text-sm text-gray-600 mb-5">
                Are you sure you want to delete {selectedIds.length} selected {selectedIds.length !== 1 ? "reports" : "report"}?
              </p>

              <div className="flex justify-end gap-3">
                {/* CANCEL */}
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setSelectedIds([]);
                  }}
                  disabled={isDeleting}
                  className="px-4 py-2 text-sm bg-gray-200 rounded-md hover:bg-gray-300 disabled:opacity-50"
                >
                  Cancel
                </button>

                {/* DELETE */}
                <button
                  onClick={handleDeleteSelected}
                  disabled={isDeleting}
                  className="px-4 py-2 text-sm bg-red-500 text-white rounded-md hover:bg-red-600 disabled:opacity-50 flex items-center gap-2"
                >
                  {isDeleting && (
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  )}
                  {isDeleting ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        )}
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          pauseOnHover
          draggable={false}
          theme="light"
        />
      </div>
    </div>
  );
};

export default ViewReportIssue;